import { Form } from 'antd';
import Box from 'components/Box';
import FormItemValue from 'components/Form/FormItemValue';
import Modal from 'components/Modal';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { getTimeSections } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/QuickBookingV2/helpers';
import { ModalDurations, useModalDurationsRef } from './ModalDurations';
export type ModalChangeTimeRef = {
  open: (configs: { serviceId: string, serviceName: string, startTime: string, timeslots?: string[], duration?: number, id?: string }, callback?: (values: IModalChangeTimeFormValues) => void) => void;
};
export type IModalChangeTimeFormValues = {
  id: string,
  serviceId: string,
  startTime: string,
  timeslots: string[],
  duration: number,
};
type Props = {
  onSubmit?: (values: IModalChangeTimeFormValues) => void;
};

const durations = [15, 30, 45, 60, 75, 90, 105, 120];
export const useModalChangeTimeRef = () => useRef<ModalChangeTimeRef>(null);
const ModalChangeTime = forwardRef<ModalChangeTimeRef, Props>(({ onSubmit }, ref) => {
  const [visible, setVisible] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const callBackRef = useRef<Props['onSubmit'] | undefined>(undefined);
  const [form] = Form.useForm();
  const onClose = () => setVisible(false);
  const handleSubmit = (values: any) => {
    const formValues = values as IModalChangeTimeFormValues;
    onSubmit?.(formValues);
    callBackRef.current?.(formValues);
    onClose();
  };
  useImperativeHandle(ref, () => ({
    open: (configs, callback) => {
      form.resetFields();
      callBackRef.current = callback || undefined;
      form.setFieldsValue({
        id: configs.id,
        serviceId: configs.serviceId,
        serviceName: configs.serviceName,
        startTime: configs.startTime,
        timeslots: configs?.timeslots,
        duration: configs?.duration,
      });
      setServiceName(configs.serviceName);
      setVisible(true);
    },
  }));
  return (
    <Modal noneBodyStyle width={1200} visible={visible} onSubmit={() => form.submit()} modalTitle={`${serviceName}`} v2 onClose={onClose}>
      <Container form={form} onFinish={handleSubmit}>
        <ContainerLeftBox>
          <div className="section-label">BEGIN TIME SERVICES:</div>
          <Form.Item name={'id'} noStyle />
          <Form.Item name={'serviceId'} noStyle />
          <Form.Item name={'startTime'} noStyle />
          <FormItemValue name={'timeslots'} render={(timeslots) => {
            const sections = getTimeSections(timeslots);
            return (<>
              {sections.morning.length > 0 && <div className='time-slot-section'>
                <div className="section-sublabel">Morning</div>
                <Form.Item noStyle name={'startTime'}>
                  <StartTimeChange timeslots={sections.morning} />
                </Form.Item>
              </div>}
              {sections.afternoon.length > 0 && <div className='time-slot-section'>
                <div className="section-sublabel">Afternoon</div>
                <Form.Item noStyle name={'startTime'}>
                  <StartTimeChange timeslots={sections.afternoon} />
                </Form.Item>
              </div>}
              {sections.evening.length > 0 && <div className='time-slot-section'>
                <div className="section-sublabel">Evening</div>
                <Form.Item noStyle name={'startTime'}>
                  <StartTimeChange timeslots={sections.evening} />
                </Form.Item>
              </div>}
            </>);
          }} />
        </ContainerLeftBox>
        <ContainerRightBox>
          <div className="section-label">CHANGE THE SERVICE TIME:</div>
          <Form.Item name={'duration'} noStyle >
            <Durations />
          </Form.Item>
        </ContainerRightBox>
      </Container>
    </Modal>
  );
});
const StartTimeChange = ({ value, onChange, timeslots = [] }: { value?: string, onChange?: (value?: string) => void; timeslots?: string[] }) => {
  return (
    <ContainerTimeSlot>
      {timeslots.map((o: string) => (
        <button type='button' onClick={() => onChange?.(o)} key={o} className={`start-time-item ${value === o ? 'active' : ''}`}>
          <span>{o}</span>
        </button>
      ))}
    </ContainerTimeSlot>
  );
};
const Durations = ({ value, onChange }: { value?: number, onChange?: (value?: number) => void; }) => {
  const modalRef = useModalDurationsRef();

  return (
    <ContainerTimeSlot className='durations'>
      {durations.map((o: number) => (
        <button type='button' onClick={() => onChange?.(o)} key={o} className={`start-time-item ${value === o ? 'active' : ''}`}>
          <span>{o}m</span>
        </button>
      ))}
      <button type='button' onClick={() => modalRef.current?.open()} className={`start-time-item ${!durations.includes(value || 0) ? 'active' : ''}`}>
        <span>Custom {(!!value && !durations.includes(value || 0)) ? ` (${value || 0}m)` : ''}</span>
      </button>
      <ModalDurations ref={modalRef} onSubmit={(o) => onChange?.(o)} />
    </ContainerTimeSlot>
  );
};
ModalChangeTime.displayName = 'ModalChangeTime';
export { ModalChangeTime };
const ContainerLeftBox = styled(Box)`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
  background: #FFF;
  box-shadow: 4px 0 8px 0 rgba(0, 0, 0, 0.15);
`;
const ContainerRightBox = styled(Box)`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  width: 350px;
`;
const ContainerTimeSlot = styled.div`
  align-self: stretch;
  display: grid;
  grid-template-columns: repeat(auto-fill,minmax(130px, 1fr));
  gap: 8px;
  .start-time-item {
    display: flex;
    padding: 16px 8px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 5px;
    border: 1px solid #CCD4DC;
    background: #FFF;
    span {
      color: #1D2129;
      text-align: center;
      font-family: Poppins;
      font-size: 22px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }

    &.active {
      border: 1px solid #2D6772;
      background: #2D6772;
      span {
        color: #FFF;
      }
    }
  }

  &.durations {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    .start-time-item {
      align-self: stretch;
    }
  }
`;
const Container = styled(Form)`
display: flex;
gap: 8px;
.gift-card-number-input {
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  align-self: stretch;
  font-size: 30px;
  height: unset;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  border: none;
  outline: none;
  box-shadow: none;
  &.fs-big {
    font-size: 55px;
  }
  &.fs-medium {
    font-size: 45px;
  }
    
}

.section-label {
  color: #505050;
  font-family: Poppins;
  font-size: 22px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.time-slot-section {
  display: flex;
  padding: 8px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  background: #F6F7FC;
  margin-bottom: 8px;
}

.section-sublabel {
  color: #505050;
  text-align: left;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin-top: 8px;
  margin-bottom: 8px;

}
`;